// Homework creation/edit modal

import { createHomework, updateHomework, loadHomeworkWithExercises, deleteHomework } from './data.js';
import { renderScheduleBlock, scheduleSummary } from './schedule.js';
import { renderExerciseRows } from './exercises.js';
import { saveHomeworkTemplate } from './templates.js';
import { injectHomeworkStyles } from './styles.js';
import { carryAttachmentsToChild, copyAttachmentsToTemplate } from './attachments.js';
import { loadExerciseTemplates, loadHomeworkTemplates } from './templates.js';
// SC-A3: matcher imported into the bundle now (explicit .mjs extension — esbuild honors it,
// default resolveExtensions lacks .mjs). The function stays pure; the modal fetches + passes arrays.
import { matchTemplates, namesEquivalent } from '../notes/match.mjs';

const T = (k, p) => window.HUD?.T?.(k, p) || k;

export function mountHomeworkCreateModal(opts = {}) {
  injectHomeworkStyles();
  const { childId, homeworkId, template, matchText } = opts;
  const H = window.HUD || {};
  const child = H.DB?.children?.find(c => c.id === childId);
  const childName = child?.name || 'Patient';
  const isEdit = !!homeworkId;

  const state = {
    homework: {
      childId,
      householdId: child?.householdId || H.session?.householdId || null,
      title: '',
      description: '',
      recurrence: 'daily',
      specificDays: [],
      timeOfDay: 'morning',  // C1: default to 'morning', not ''
      durationType: 'open_ended',
      endDate: null,
      isPinned: false,
      attachedFileUrls: [],
      attachedFilePaths: [],
      attachedFileNames: [],
    },
    exercises: [
      { title: '', instructions: '', reps: null, sets: null, durationSeconds: null, measureUnit: null, overrideRecurrence: null, overrideSpecificDays: null, overrideTimeOfDay: null, attachedFileUrls: [], attachedFilePaths: [], attachedFileNames: [], _ui: { expanded: false } }
    ],
    saving: false,
    dirty: false,
  };

  // Pre-fill from template
  // Schedule fields (recurrence, specific_days, duration_type, time_of_day) on homework_templates
  // are dormant per locked design decision (May 7, 2026). Templates carry title + exercises only;
  // schedule is configured fresh per patient. Columns retained for potential HIPAA-pass cleanup.
  if (template && !isEdit) {
    // SC-A1b (amended): the template's NAME is its library identity and does NOT become the created
    // homework's title — the instance is a dated event, so the title defaults to today's date below
    // (date wins in EVERY create mode). Only description + exercises carry over from the template.
    state.homework.description = template.description || '';
    if (template.exercises_json?.length) {
      state.exercises = template.exercises_json.map(ex => ({
        title: ex.title || '', instructions: ex.instructions || '', reps: ex.reps || null, sets: ex.sets || null,
        durationSeconds: ex.durationSeconds || null, measureUnit: ex.measureUnit || null,
        overrideRecurrence: ex.overrideRecurrence || null, overrideSpecificDays: ex.overrideSpecificDays || null,
        overrideTimeOfDay: ex.overrideTimeOfDay || null, attachedFileUrls: [], attachedFilePaths: ex.attachedFilePaths ? [...ex.attachedFilePaths] : [], attachedFileNames: ex.attachedFileNames ? [...ex.attachedFileNames] : [], _ui: { expanded: false },
      }));
    }
  }

  // SC-A1b: default the homework title to today's DATE — mirrors the notes default-title pattern
  // (src/features/notes/helpers.js `formatNoteTitle`), reusing the SAME `_locDate` primitive + options,
  // DATE ONLY (no time-of-day). Locale-aware via `_locDate` (bridged): "Mar 3 · 2026" (EN) /
  // "3 במרץ · 2026" (HE). Applies to EVERY create mode — including template-use (the template name is
  // NOT the instance title). Edit mode is never touched (existing title is loaded/preserved). Set as a
  // REAL value below (not a placeholder), so an untouched save persists the date string, never empty.
  if (!isEdit) {
    const _d = new Date();
    const _ld = H._locDate;
    const _datePart = (typeof _ld === 'function')
      ? _ld(_d, { month: 'short', day: 'numeric' })
      : _d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    state.homework.title = _datePart + ' · ' + _d.getFullYear();
  }

  // ── Modal shell ──
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;z-index:450;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:16px;';

  const card = document.createElement('div');
  card.className = 'hw2-modal';

  function closeModal() {
    if (state.dirty) {
      const c = H.openConfirm;
      if (c) { c(T('hw2_discard_title'), T('hw2_discard_body'), true, () => overlay.remove()); return; }
    }
    overlay.remove();
  }

  // ── Sticky header ──
  const header = document.createElement('div');
  header.className = 'hw2-header';

  const closeBtn = document.createElement('button');
  closeBtn.textContent = '\u2715';
  closeBtn.style.cssText = 'width:32px;height:32px;border-radius:50%;border:none;background:#e8f4f2;cursor:pointer;font-size:16px;color:#64748b;display:flex;align-items:center;justify-content:center;flex-shrink:0;';
  closeBtn.onclick = closeModal;
  header.appendChild(closeBtn);

  const titleEl = document.createElement('div');
  titleEl.style.cssText = 'flex:1;text-align:center;font-size:15px;font-weight:700;color:#0f1a18;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding:0 8px;';
  titleEl.textContent = (isEdit ? T('hw2_edit_title') : T('hw2_new_title')) + ' \u00b7 ' + childName;
  header.appendChild(titleEl);

  // C6: "Save quietly" button (create mode only, skips notification)
  let quietBtn = null;
  if (!isEdit) {
    quietBtn = document.createElement('button');
    quietBtn.textContent = T('hw2_save_quiet');
    quietBtn.style.cssText = 'padding:6px 10px;border:1.5px solid #d1e0dd;border-radius:8px;background:#fff;color:#64748b;font-size:12px;cursor:pointer;font-family:inherit;font-weight:500;flex-shrink:0;';
    quietBtn.disabled = true;
    header.appendChild(quietBtn);
  }

  const saveBtn = document.createElement('button');
  saveBtn.textContent = isEdit ? T('hw2_save_changes') : T('hw2_save_send');
  saveBtn.style.cssText = 'padding:6px 14px;border:none;border-radius:8px;background:#0d9488;color:#fff;font-size:13px;cursor:pointer;font-family:inherit;font-weight:600;flex-shrink:0;';
  saveBtn.disabled = true;
  header.appendChild(saveBtn);

  card.appendChild(header);

  // ── Body ──
  const body = document.createElement('div');
  body.className = 'hw2-body';

  // Title input
  const titleInp = document.createElement('input');
  titleInp.type = 'text';
  titleInp.className = 'hw2-input';
  titleInp.placeholder = T('hw2_title_placeholder');
  titleInp.maxLength = 200;
  titleInp.dir = 'auto'; // SC-A1b: locale-aware default title renders LTR (EN) / RTL (HE) correctly
  if (!isEdit) titleInp.value = state.homework.title; // SC-A1b: real, editable prefilled title (date default or template); edit mode fills at load below
  titleInp.style.cssText += 'font-weight:600;font-size:16px;margin-bottom:12px;';
  titleInp.oninput = () => { state.homework.title = titleInp.value; state.dirty = true; _updateSaveBtn(); };
  body.appendChild(titleInp);

  // Notes for parents
  const notesLabel = document.createElement('div');
  notesLabel.className = 'hw2-section-label';
  notesLabel.textContent = T('hw2_notes_label');
  body.appendChild(notesLabel);

  const notesInp = document.createElement('textarea');
  notesInp.className = 'hw2-input hw2-textarea';
  notesInp.placeholder = T('hw2_notes_placeholder');
  notesInp.style.marginBottom = '8px';
  notesInp.oninput = () => { state.homework.description = notesInp.value; state.dirty = true; };
  body.appendChild(notesInp);

  // SC-A3: homework-template match banner mounts here (above the exercise list). Empty unless a
  // homework_templates match fires when opened from a note; dismissible; never a silent takeover.
  const bannerContainer = document.createElement('div');
  body.appendChild(bannerContainer);

  // Exercises section
  const exLabel = document.createElement('div');
  exLabel.className = 'hw2-section-label';
  exLabel.style.marginTop = '8px';
  function _updateExLabel() { exLabel.textContent = T('hw2_exercises_count', { n: state.exercises.length }); }
  _updateExLabel();
  body.appendChild(exLabel);

  let exContainer = document.createElement('div');
  body.appendChild(exContainer);

  function _renderEx() {
    const newEl = renderExerciseRows(state.exercises, (exs) => { state.exercises = exs; state.dirty = true; _updateSaveBtn(); _updateExLabel(); }, state.homework, { childId, H });
    exContainer.replaceWith(newEl);
    exContainer = newEl;
  }

  // C5: schedule section with re-render helper
  const schLabel = document.createElement('div');
  schLabel.className = 'hw2-section-label';
  schLabel.textContent = T('hw2_when_label');
  schLabel.style.marginTop = '16px';
  body.appendChild(schLabel);

  let schContainer = document.createElement('div');
  body.appendChild(schContainer);

  function _renderSch() {
    const newEl = renderScheduleBlock(state.homework, (s) => { Object.assign(state.homework, s); state.dirty = true; });
    schContainer.replaceWith(newEl);
    schContainer = newEl;
  }

  // Save as template (create mode only)
  let _saveAsTemplate = false;
  let _tmplNameInp = null;
  if (!isEdit) {
    const tmplWrap = document.createElement('div');
    tmplWrap.style.cssText = 'margin-top:16px;padding-top:12px;border-top:1px solid #f1f5f9;';
    const tmplRow = document.createElement('div');
    tmplRow.style.cssText = 'display:flex;align-items:center;gap:10px;cursor:pointer;';
    const tmplChk = document.createElement('div');
    tmplChk.className = 'hw2-chk';
    tmplRow.onclick = () => {
      _saveAsTemplate = !_saveAsTemplate;
      tmplChk.className = 'hw2-chk' + (_saveAsTemplate ? ' checked' : '');
      tmplChk.textContent = _saveAsTemplate ? '\u2713' : '';
      tmplNameWrap.style.display = _saveAsTemplate ? 'block' : 'none';
    };
    tmplRow.appendChild(tmplChk);
    tmplRow.appendChild(document.createTextNode(T('hw2_save_template')));
    tmplWrap.appendChild(tmplRow);
    const tmplNameWrap = document.createElement('div');
    tmplNameWrap.style.cssText = 'display:none;margin-top:8px;';
    _tmplNameInp = document.createElement('input');
    _tmplNameInp.type = 'text';
    _tmplNameInp.className = 'hw2-input';
    _tmplNameInp.placeholder = T('hw2_template_name_placeholder');
    tmplNameWrap.appendChild(_tmplNameInp);
    tmplWrap.appendChild(tmplNameWrap);
    body.appendChild(tmplWrap);
  }

  // Delete button (edit mode only)
  if (isEdit) {
    const delWrap = document.createElement('div');
    delWrap.style.cssText = 'margin-top:24px;padding-top:16px;border-top:1px solid #f1f5f9;text-align:center;';
    const delBtn = document.createElement('button');
    delBtn.textContent = T('hw2_delete');
    delBtn.style.cssText = 'background:none;border:none;cursor:pointer;color:#ef4444;font-size:13px;font-weight:500;font-family:inherit;';
    delBtn.onclick = () => {
      const c = H.openConfirm;
      if (c) c(T('hw2_delete_title'), T('hw2_delete_body'), true, async () => {
        try {
          await deleteHomework(homeworkId);
          overlay.remove();
          const all = H.DB?.homeworkTasks || [];
          const i = all.findIndex(t => t.id === homeworkId);
          if (i > -1) { all[i].status = 'deleted'; H.DB.homeworkTasks = all; }
          H.re?.(); H.toast?.(T('hw2_deleted'));
        } catch (e) { H.toast?.(T('hw2_delete_error'), 'error'); }
      });
    };
    delWrap.appendChild(delBtn);
    body.appendChild(delWrap);
  }

  card.appendChild(body);
  overlay.appendChild(card);
  overlay.onclick = (e) => { if (e.target === overlay) closeModal(); };
  // SC4b: expose this modal's dirty state to the SW-update reloader for its open lifetime, and clear
  // it on every close path (all funnel through overlay.remove()), then drain any deferred SW reload.
  if (H.S) H.S._hwCreateState = state;
  const _overlayRemove = overlay.remove.bind(overlay);
  overlay.remove = () => {
    try { if (H.S && H.S._hwCreateState === state) H.S._hwCreateState = null; } catch (_) {}
    _overlayRemove();
    try { H._maybeSwReload?.(); } catch (_) {}
  };
  document.body.appendChild(overlay);

  // ── Validation + save ──
  function _updateSaveBtn() {
    const hasTitle = state.homework.title.trim().length > 0;
    const hasExTitle = state.exercises.some(ex => (ex.title || '').trim().length > 0);
    const ok = hasTitle && hasExTitle && !state.saving;
    saveBtn.disabled = !ok;
    saveBtn.style.opacity = ok ? '1' : '0.5';
    if (quietBtn) { quietBtn.disabled = !ok; quietBtn.style.opacity = ok ? '1' : '0.5'; }
  }

  async function _doSave(notify) {
    const hw = state.homework;
    if (!hw.title.trim()) { H.toast?.(T('hw_title_required'), 'error'); return; }
    if (!state.exercises.some(ex => (ex.title || '').trim())) { H.toast?.(T('hw2_exercise_required'), 'error'); return; }
    if (hw.recurrence === 'specific_days' && (!hw.specificDays || !hw.specificDays.length)) { H.toast?.(T('hw_select_day'), 'error'); return; }
    if (hw.durationType === 'end_date' && !hw.endDate) { H.toast?.(T('hw_select_end_date'), 'error'); return; }
    const times = (hw.timeOfDay || '').split(',').filter(Boolean);
    if (!times.length) { H.toast?.(T('hw_select_1_time'), 'error'); return; }

    state.saving = true;
    _updateSaveBtn();
    saveBtn.textContent = T('btn_loading');
    if (quietBtn) quietBtn.textContent = T('btn_loading');

    // C7: filter empty exercises with feedback
    const allEx = state.exercises;
    const cleanExercises = allEx.filter(ex => (ex.title || '').trim()).map(({ _ui, ...rest }) => rest);
    const dropped = allEx.length - cleanExercises.length;
    if (dropped > 0) H.toast?.(T('hw2_removed_empty', { n: dropped }));

    // SC-A1c: copy any carried template attachments onto THIS child's homework path (best-effort,
    // BEFORE the isEdit fork so add-from-library during an edit carries too). A failed copy drops that
    // one file and is surfaced after save — the clinical payload is never lost to a flaky storage copy.
    let _attachWarnings = [];
    try { _attachWarnings = await carryAttachmentsToChild(cleanExercises, childId, H); }
    catch (e) { console.error('❌ attachment carry:', e); }

    try {
      if (isEdit) {
        await updateHomework({ homeworkId, homework: hw, exercises: cleanExercises });
        const all = H.DB?.homeworkTasks || [];
        const idx = all.findIndex(t => t.id === homeworkId);
        if (idx > -1) {
          all[idx] = { ...all[idx], title: hw.title, description: hw.description, recurrence: hw.recurrence, specificDays: hw.specificDays || [], durationType: hw.durationType, endDate: hw.endDate, timeOfDay: hw.timeOfDay, isPinned: hw.isPinned, attachedFileUrls: hw.attachedFileUrls, attachedFilePaths: hw.attachedFilePaths, attachedFileNames: hw.attachedFileNames };
          H.DB.homeworkTasks = all;
        }
        overlay.remove(); H.re?.(); H.toast?.(T('hw_task_updated'));
      } else {
        const result = await createHomework({ homework: hw, exercises: cleanExercises });
        // TODO(Phase 6e): Remove this call once v1 reads are gone. See PHASE_6_7_DEFERRED.md.
        if (typeof H._generateOccurrences === 'function') {
          try { await H._generateOccurrences(result.homeworkRow); } catch (e) { console.error('generate occurrences:', e); }
        }
        // C4: use real created_at from DB response
        const all = H.DB?.homeworkTasks || [];
        if (result.homeworkRow) all.unshift({ id: result.homeworkRow.id, childId: result.homeworkRow.child_id, householdId: result.homeworkRow.household_id, specialistId: result.homeworkRow.specialist_id, specialistName: result.homeworkRow.specialist_name, title: hw.title, description: hw.description || '', recurrence: hw.recurrence, specificDays: hw.specificDays || [], durationType: hw.durationType, endDate: hw.endDate, timeOfDay: hw.timeOfDay, isPinned: hw.isPinned, isPaused: false, status: 'active', attachedFileUrls: hw.attachedFileUrls, attachedFilePaths: hw.attachedFilePaths, attachedFileNames: hw.attachedFileNames, createdAt: result.homeworkRow.created_at });
        H.DB.homeworkTasks = all;
        // Save as template (if checked) — SC-A1c: copy each exercise's attachments into the specialist's
        // template prefix so the homework template owns INDEPENDENT objects (model b), storing those
        // template-owned paths (camelCase) in exercises_json. Best-effort per file; urls cleared.
        if (_saveAsTemplate) {
          try {
            const tmplExercises = [];
            for (const ex of cleanExercises) {
              const { paths, names } = await copyAttachmentsToTemplate(ex.attachedFilePaths, ex.attachedFileNames, H);
              tmplExercises.push({ ...ex, attachedFilePaths: paths, attachedFileNames: names, attachedFileUrls: [] });
            }
            await saveHomeworkTemplate({ title: _tmplNameInp?.value?.trim() || hw.title, description: hw.description, exercisesJson: tmplExercises });
          } catch (e) { console.error('save template:', e); }
        }
        // Notify parent (skip if quiet save)
        if (notify) {
          try {
            const childObj = H.DB?.children?.find(c => c.id === childId);
            await H.notifyOtherParty?.('homework', T('notif_new_task', { child: childObj?.name || '', title: hw.title.slice(0, 42) }), childId, 'homework', null, null, T('notif_homework_count', { n: '{n}', name: childObj?.name || '' }), H._nTw?.('notif_new_task', { child: childObj?.name || '', title: hw.title.slice(0, 42) }), H._nTw?.('notif_homework_count', { n: '{n}', name: childObj?.name || '' }));
          } catch (e) { console.error('notify:', e); }
        }
        overlay.remove(); H.re?.(); H.toast?.(notify ? T('hw_task_assigned') : T('hw2_saved_quiet'));
      }
      if (_attachWarnings.length) H.toast?.(T('templates_attach_carry_warn', { names: _attachWarnings.join(', ') }), 'error');
    } catch (e) {
      console.error('save homework:', e);
      H.toast?.(T('hw_could_not_save_task'), 'error');
      state.saving = false;
      saveBtn.textContent = isEdit ? T('hw2_save_changes') : T('hw2_save_send');
      if (quietBtn) quietBtn.textContent = T('hw2_save_quiet');
      _updateSaveBtn();
    }
  }

  saveBtn.onclick = () => _doSave(true);
  if (quietBtn) quietBtn.onclick = () => _doSave(false);

  // ── SC-A3: match templates against the note's clinical text (fetch on open, from-note only) ──
  const _blankEx = () => ({ title: '', instructions: '', reps: null, sets: null, durationSeconds: null, measureUnit: null, overrideRecurrence: null, overrideSpecificDays: null, overrideTimeOfDay: null, attachedFileUrls: [], attachedFilePaths: [], attachedFileNames: [], _ui: { expanded: false } });

  // Build an exercise-list object from an exercise_templates ROW (snake_case), matching the shape
  // the add-from-library path (_useExerciseTemplate) produces — so _doSave's carryAttachmentsToChild
  // copies the template-owned files onto the child exactly as template-use does (A1c, path-first).
  function _buildExFromRow(row, sentence) {
    return {
      title: row.name || '', instructions: row.instructions || '', reps: row.reps || null, sets: row.sets || null,
      durationSeconds: row.duration_seconds || null, measureUnit: row.measure_unit || null,
      overrideRecurrence: null, overrideSpecificDays: null, overrideTimeOfDay: null,
      attachedFileUrls: [], // A1c: never carry the row's expiring url; sign fresh from path
      attachedFilePaths: row.attached_file_paths ? [...row.attached_file_paths] : [],
      attachedFileNames: row.attached_file_names ? [...row.attached_file_names] : [],
      _ui: { expanded: false, matchedSentence: sentence || '' },
    };
  }

  // Build an exercise-list object from a homework template's exercises_json entry (camelCase) —
  // identical to the construction-time template hydration (lines above), factored so the banner's
  // "Use" applies the same semantics.
  function _exFromTemplateEx(ex) {
    return {
      title: ex.title || '', instructions: ex.instructions || '', reps: ex.reps || null, sets: ex.sets || null,
      durationSeconds: ex.durationSeconds || null, measureUnit: ex.measureUnit || null,
      overrideRecurrence: ex.overrideRecurrence || null, overrideSpecificDays: ex.overrideSpecificDays || null,
      overrideTimeOfDay: ex.overrideTimeOfDay || null, attachedFileUrls: [],
      attachedFilePaths: ex.attachedFilePaths ? [...ex.attachedFilePaths] : [], attachedFileNames: ex.attachedFileNames ? [...ex.attachedFileNames] : [], _ui: { expanded: false },
    };
  }

  let _matchedExercises = [];
  function _isPristineList() {
    if (state.exercises.length !== 1) return false;
    const e = state.exercises[0];
    return !(e.title || '').trim() && !e.instructions && !(e.attachedFilePaths && e.attachedFilePaths.length);
  }
  function _applyMatchedExercises() {
    if (!_matchedExercises.length) return; // decision 4: zero matches → modal stays blank/date-titled
    state.exercises = _isPristineList() ? _matchedExercises.slice() : state.exercises.concat(_matchedExercises);
    _renderEx(); _updateExLabel(); _updateSaveBtn();
  }
  function _applyHomeworkTemplate(tmplRow) {
    // Homework-template match wins as the base (A5-final); matched exercises NOT already inside it
    // stay as additions (same-name suppression via namesEquivalent — prefix/stem aware).
    if (!state.homework.description.trim() && tmplRow.description) {
      state.homework.description = tmplRow.description;
      notesInp.value = tmplRow.description; // guard: only when the user hasn't typed notes
    }
    const tmplExs = (tmplRow.exercises_json || []).map(_exFromTemplateEx);
    const base = tmplExs.length ? tmplExs : [_blankEx()];
    const extras = _matchedExercises.filter(me => !base.some(te => namesEquivalent(te.title, me.title)));
    state.exercises = base.concat(extras);
    _renderEx(); _updateExLabel(); _updateSaveBtn();
  }
  function _renderBanner(tmplRow) {
    bannerContainer.innerHTML = '';
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;align-items:center;gap:10px;margin:4px 0 12px;padding:10px 12px;background:#f0fdf9;border:1px solid #99f6e4;border-radius:10px;';
    const txt = document.createElement('div');
    txt.dir = 'auto'; // SC-A4: banner is HE with a possibly-Latin {name} — let the browser resolve direction
    txt.style.cssText = 'flex:1;font-size:13px;color:#0f766e;line-height:1.35;';
    txt.textContent = T('hw2_program_banner', { name: tmplRow.title || '' });
    bar.appendChild(txt);
    const useBtn = document.createElement('button');
    useBtn.textContent = T('hw2_program_use');
    useBtn.style.cssText = 'padding:6px 12px;border:none;border-radius:8px;background:#0d9488;color:#fff;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit;flex-shrink:0;';
    useBtn.onclick = () => { _applyHomeworkTemplate(tmplRow); bannerContainer.innerHTML = ''; };
    bar.appendChild(useBtn);
    const dismiss = document.createElement('button');
    dismiss.textContent = '✕';
    dismiss.title = T('hw2_program_dismiss');
    dismiss.style.cssText = 'width:26px;height:26px;border:none;border-radius:50%;background:transparent;color:#0f766e;font-size:14px;cursor:pointer;flex-shrink:0;';
    dismiss.onclick = () => { bannerContainer.innerHTML = ''; };
    bar.appendChild(dismiss);
    bannerContainer.appendChild(bar);
  }
  async function _maybeMatchFromNote() {
    if (!matchText || isEdit || template) return; // fetch ONLY when opened from a note
    try {
      const [exRows, hwRows] = await Promise.all([loadExerciseTemplates(), loadHomeworkTemplates()]);
      const res = matchTemplates(matchText, exRows || [], hwRows || []);
      const exById = new Map((exRows || []).map(r => [r.id, r]));
      _matchedExercises = res.exerciseMatches.map(m => { const row = exById.get(m.templateId); return row ? _buildExFromRow(row, m.sentence) : null; }).filter(Boolean);
      _applyMatchedExercises();
      if (res.homeworkMatches.length) {
        const hwById = new Map((hwRows || []).map(r => [r.id, r]));
        const top = hwById.get(res.homeworkMatches[0].templateId);
        if (top) _renderBanner(top);
      }
    } catch (e) { console.error('❌ note→template match:', e); }
  }

  // ── Edit mode: load existing data ──
  if (isEdit) {
    // F1: block interaction during load
    body.style.opacity = '0.5';
    body.style.pointerEvents = 'none';
    saveBtn.disabled = true;
    loadHomeworkWithExercises(homeworkId).then(result => {
      body.style.opacity = '1';
      body.style.pointerEvents = '';
      if (!result) { H.toast?.(T('hw2_load_error'), 'error'); overlay.remove(); return; }
      const hw = result.homework;
      state.homework.title = hw.title || '';
      state.homework.description = hw.description || '';
      state.homework.recurrence = hw.recurrence || 'daily';
      state.homework.specificDays = hw.specific_days || [];
      state.homework.timeOfDay = hw.time_of_day || 'morning';
      state.homework.durationType = hw.duration_type || 'open_ended';
      state.homework.endDate = hw.end_date || null;
      state.homework.isPinned = hw.is_pinned || false;
      // Legacy migration: homework-level attachments → exercise[0]
      const _legacyUrls = hw.attached_file_urls || [];
      const _legacyPaths = hw.attached_file_paths || [];
      const _legacyNames = hw.attached_file_names || [];
      // Homework-level state stays empty going forward; new writes will persist empty arrays
      state.homework.attachedFileUrls = [];
      state.homework.attachedFilePaths = [];
      state.homework.attachedFileNames = [];

      titleInp.value = state.homework.title;
      notesInp.value = state.homework.description;

      state.exercises = (result.exercises || []).map(ex => ({
        id: ex.id,
        title: ex.title || '',
        instructions: ex.instructions || '',
        reps: ex.reps,
        sets: ex.sets,
        durationSeconds: ex.duration_seconds,
        measureUnit: ex.measure_unit,
        overrideRecurrence: ex.override_recurrence,
        overrideSpecificDays: ex.override_specific_days,
        overrideTimeOfDay: ex.override_time_of_day,
        attachedFileUrls: ex.attached_file_urls || [],
        attachedFilePaths: ex.attached_file_paths || [],
        attachedFileNames: ex.attached_file_names || [],
        _ui: { expanded: false },
      }));
      if (!state.exercises.length) state.exercises.push({ title: '', instructions: '', reps: null, sets: null, durationSeconds: null, measureUnit: null, overrideRecurrence: null, overrideSpecificDays: null, overrideTimeOfDay: null, attachedFileUrls: [], attachedFilePaths: [], attachedFileNames: [], _ui: { expanded: false } });

      // Apply legacy migration to exercise[0] (only if legacy lists are non-empty AND exercise[0] doesn't already have attachments)
      if ((_legacyPaths.length || _legacyUrls.length) && state.exercises[0]) {
        const e0 = state.exercises[0];
        if (!e0.attachedFilePaths.length && !e0.attachedFileUrls.length) {
          e0.attachedFilePaths = [..._legacyPaths];
          e0.attachedFileUrls = [..._legacyUrls];
          e0.attachedFileNames = [..._legacyNames];
        }
      }

      _renderEx();
      _renderSch();
      _updateSaveBtn();
      // F2: mark clean after populating from DB
      state.dirty = false;
    });
  } else {
    _renderEx();
    _renderSch();
    _updateSaveBtn();
    _maybeMatchFromNote(); // SC-A3: async; no-op unless opened from a note (matchText set)
  }
}
